import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

// Define the ApprovalLevel interface
export interface ApprovalLevel {
  id: string;
  workflow_template: string;
  level: number;
  level_number: number;
  description: string;
  approval_mode: 'user_role' | 'specific_user';
  approval_mode_display?: string;
  approval_type: 'sequential';
  user_role?: string;
  role_details?: {
    id: string;
    name: string;
    description: string;
  };
  specific_user: string;
  reporting_level?: number;
  reminder_sla_value: number;
  reminder_sla_unit: 'minutes' | 'hours' | 'days';
  escalation_sla_value: number;
  escalation_sla_unit: 'minutes' | 'hours' | 'days';
  approval_required: boolean;
  created_at: string;
  updated_at: string;
  modifiable_fields: string[];
}

// Role interface
export interface Role {
  id: string;
  name: string;
  description?: string;
  user_count?: number;
}

// Reporting Level interface
export interface ReportingLevel {
  level: number;
  description: string;
}

// Reporting Chain interface
export interface ReportingChain {
  user_id: string;
  user_name: string;
  chain: {
    level: number;
    user_id: string;
    user_name: string;
    title?: string;
  }[];
}

// SLA Unit interface
export interface SlaUnit {
  value: 'minutes' | 'hours' | 'days';
  label: string;
}

// Pagination interface for approval levels
export interface ApprovalLevelPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApprovalLevel[];
}

// Define types for request and response
export interface ApprovalLevelCreateInput {
  workflow_template: string;
  description: string;
  approval_mode: 'user_role' | 'specific_user';
  approval_type: 'sequential';
  user_role?: string;
  specific_user?: string;
  reporting_level?: number;
  reminder_sla_value: number;
  reminder_sla_unit: 'minutes' | 'hours' | 'days';
  escalation_sla_value: number;
  escalation_sla_unit: 'minutes' | 'hours' | 'days';
  approval_required: boolean;
  modifiable_fields?: string[];
}

// Make all fields optional for updates
export interface ApprovalLevelUpdateInput extends Partial<Omit<ApprovalLevelCreateInput, 'workflow_template'>> {
  // Additional fields specific to updates can be added here
  update_reason?: string;
}

// Create API
export const workflowApprovalApi = createApi({
  reducerPath: 'workflowApprovalApi',
  baseQuery: createExtendedTokenBaseQuery('workflows/'),
  tagTypes: ['ApprovalLevel', 'Role', 'ReportingLevel'],
  endpoints: (builder) => ({
    // Get approval levels for a workflow template
    getApprovalLevels: builder.query<ApprovalLevel[], string>({
      query: (workflowTemplateId) => `workflow-templates/${workflowTemplateId}/approval-levels/custom/`,
      providesTags: (result, error, workflowTemplateId) => [
        { type: 'ApprovalLevel', id: workflowTemplateId },
        'ApprovalLevel'
      ],
    }),
    
    // Get approval level by ID
    getApprovalLevelById: builder.query<ApprovalLevel, string>({
      query: (id) => `workflow-approval-levels/${id}/`,
      providesTags: (result, error, id) => [{ type: 'ApprovalLevel', id }],
    }),
    
    // Create approval level
    createApprovalLevel: builder.mutation<ApprovalLevel, ApprovalLevelCreateInput>({
      query: (approvalLevel) => ({
        url: `workflow-templates/${approvalLevel.workflow_template}/approval-levels/custom/`,
        method: 'POST',
        body: approvalLevel,
      }),
      invalidatesTags: (result, error, { workflow_template }) => [
        { type: 'ApprovalLevel', id: workflow_template },
        'ApprovalLevel'
      ],
    }),
    
    // Update approval level
    updateApprovalLevel: builder.mutation<ApprovalLevel, { id: string; workflowTemplateId: string; approvalLevel: ApprovalLevelUpdateInput }>({
      query: ({ id, workflowTemplateId, approvalLevel }) => ({
        url: `workflow-templates/${workflowTemplateId}/approval-levels/custom/${id}/`,
        method: 'PUT',
        body: approvalLevel,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ApprovalLevel', id },
        'ApprovalLevel'
      ],
    }),
    
    // Delete approval level
    deleteApprovalLevel: builder.mutation<void, {id:string; workflowTemplateId: string}>({
      query: ({id, workflowTemplateId}) => ({
        url: `workflow-templates/${workflowTemplateId}/approval-levels/custom/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ApprovalLevel'],
    }),
    
    // Reorder approval levels
    reorderApprovalLevels: builder.mutation<void, { workflowTemplateId: string; levelIds: {id:string; level: number}[] }>({
      query: ({ workflowTemplateId, levelIds }) => ({
        url: `workflow-templates/${workflowTemplateId}/approval-levels/custom/reorder/`,
        method: 'POST',
        body: { levels: levelIds },
      }),
      invalidatesTags: (result, error, { workflowTemplateId }) => [
        { type: 'ApprovalLevel', id: workflowTemplateId },
        'ApprovalLevel'
      ],
    }),
    
    // Get all roles
    getRoles: builder.query<Role[], { search?: string; page?: number; limit?: number } | void>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params && typeof params === 'object') {
          if ('search' in params && params.search) {
            queryParams.append('search', params.search);
          }
          if ('page' in params && params.page) {
            queryParams.append('page', params.page.toString());
          }
          if ('limit' in params && params.limit) {
            queryParams.append('limit', params.limit.toString());
          }
        }
        return { url: 'approvals/roles/', params: queryParams };
      },
      providesTags: ['Role'],
    }),
    
    // Get available reporting levels
    getReportingLevels: builder.query<ReportingLevel[], void>({
      query: () => 'reporting-levels/',
      providesTags: ['ReportingLevel'],
    }),
    
    // Get user's reporting chain
    getUserReportingChain: builder.query<ReportingChain, string>({
      query: (userId) => `users/${userId}/reporting-chain/`,
    }),
    
    // Get SLA units
    getSlaUnits: builder.query<SlaUnit[], void>({
      query: () => 'sla-units/',
    }),
    
    // Get approval modes
    getApprovalModes: builder.query<{ value: string; label: string }[], void>({
      query: () => 'approvals/approval-modes/',
    }),
  }),
});

// Export hooks
export const {
  useGetApprovalLevelsQuery,
  useGetApprovalLevelByIdQuery,
  useCreateApprovalLevelMutation,
  useUpdateApprovalLevelMutation,
  useDeleteApprovalLevelMutation,
  useReorderApprovalLevelsMutation,
  useGetRolesQuery,
  useGetReportingLevelsQuery,
  useGetUserReportingChainQuery,
  useGetSlaUnitsQuery,
  useGetApprovalModesQuery,
} = workflowApprovalApi;
